import {
  AccountBookOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BellOutlined,
  BulbOutlined,
  CarOutlined,
  ClusterOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  FileProtectOutlined,
  FileSearchOutlined,
  InboxOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  OrderedListOutlined,
  PercentageOutlined,
  SafetyOutlined,
  SettingOutlined,
  TeamOutlined,
  TruckOutlined,
  UserOutlined
} from "@ant-design/icons";
import { Avatar, Badge, Button, Dropdown, Layout, Menu, Space, Switch, Typography } from "antd";
import type { MenuProps } from "antd";
import type { CSSProperties, PropsWithChildren, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { transportApi } from "../../api/transportApi";
import { useAuthStore } from "../../stores/authStore";
import { useFyStore } from "../../stores/fyStore";
import { useThemeStore } from "../../stores/themeStore";
import { getTransportPalette } from "../../theme/transportThemes";
import { HelpDrawer } from "../../shared/components/HelpDrawer";
import { GlobalSearch } from "../../shared/components/GlobalSearch";
import type { SearchNavNode } from "../../shared/components/GlobalSearch";

const { Header, Sider, Content } = Layout;

type NavLeaf = {
  key: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  permissionKeys: string[];
};

// All routable leaf paths — used for selectedKey resolution
const ALL_LEAF_PATHS: string[] = [
  "/", "/consignments", "/manifests", "/manifest-arrivals", "/gate-pass",
  "/lorry-hire", "/ewaybill", "/operations", "/reports",
  "/operations/enquiry", "/accounting/enquiry", "/inventory/enquiry",
  "/accounting", "/commission",
  "/bilty-stationery",
  "/fleet", "/drivers", "/trips", "/attachments",
  "/parties", "/users", "/master-data", "/settings", "/audit-logs",
];

// Disabled placeholder (no real route yet)
const placeholder = (label: string): NavLeaf => ({
  key: `__placeholder__${label}`,
  label,
  disabled: true,
  permissionKeys: [],
});

const menuItems = (isAdmin: boolean, perms: string[]): MenuProps["items"] => {
  const hasAny = (keys: string[]) =>
    isAdmin || keys.length === 0 || keys.some(k => perms.includes(k.toLowerCase()));

  const leaf = (item: NavLeaf) => ({
    key: item.key,
    label: item.label,
    icon: item.icon,
    disabled: item.disabled ?? false,
  });

  const operationsTransactions: NavLeaf[] = [
    { key: "/consignments",      label: "Consignment Entry",          icon: <AppstoreOutlined />,   permissionKeys: ["consignments.view"] },
    { key: "/manifests",         label: "Lorry Manifest Entry",       icon: <OrderedListOutlined />, permissionKeys: ["manifests.view"] },
    { key: "/manifest-arrivals", label: "Manifest Arrival Entry",     icon: <InboxOutlined />,      permissionKeys: ["manifests.view"] },
    { key: "/gate-pass",         label: "Gate Pass Entry",            icon: <FileProtectOutlined />, permissionKeys: ["manifests.view"] },
    { key: "/lorry-hire",        label: "Lorry Hire Challan Entry",   icon: <TruckOutlined />,      permissionKeys: ["lorryhire.view"] },
    placeholder("Challan Remark Entry"),
    placeholder("Bill Despatch Date Entry"),
    placeholder("Manifest Modification"),
    placeholder("Unloading Manifest"),
    placeholder("MR Entry (ToPay)"),
    placeholder("Claim Proposed Entry"),
    placeholder("WC Entry"),
    placeholder("Claim Entry"),
    placeholder("Consignment Remarks Entry"),
    placeholder("Bill Make (Auto)"),
    placeholder("Claim Payment"),
    placeholder("Rebate Entry"),
    placeholder("Transit Pass (In)"),
    placeholder("Transit Pass (Out)"),
    placeholder("Bill Entry (Consignment)"),
  ];

  const inventoryTransactions: NavLeaf[] = [
    { key: "/bilty-stationery", label: "Document Stationery",        icon: <FileSearchOutlined />, permissionKeys: ["inventoryissue.read", "settings.view"] },
    placeholder("Purchase Order"),
    placeholder("Purchase Challan"),
    placeholder("Stock Issue to Branch"),
    placeholder("Stock Receipt from H.O."),
    placeholder("Stock Transfer"),
    placeholder("Stationery Receipt Stock Cancel"),
    placeholder("Stationery Receipt Stock Ignore"),
    placeholder("Branch Stock Update"),
  ];

  return [
    // ── Dashboard ──────────────────────────────────────────────────────────
    { key: "/", icon: <DashboardOutlined />, label: "Dashboard" },

    // ── Operations ────────────────────────────────────────────────────────
    {
      key: "group-operations",
      className: "nav-group-operations",
      label: "Operations",
      icon: <TruckOutlined />,
      children: [
        {
          key: "ops-transactions",
          label: "Transactions",
          icon: <AppstoreOutlined />,
          children: operationsTransactions.filter(i => hasAny(i.permissionKeys) || i.disabled).map(leaf),
        },
        { key: "/operations/enquiry", label: "Enquiry", icon: <FileSearchOutlined /> },
        {
          key: "ops-reports",
          label: "Reports",
          icon: <BarChartOutlined />,
          children: [
            { key: "/reports", label: "Booking Report", icon: <BarChartOutlined /> },
          ],
        },
      ],
    },

    // ── Accounts ──────────────────────────────────────────────────────────
    {
      key: "group-accounts",
      className: "nav-group-accounts",
      label: "Accounts",
      icon: <AccountBookOutlined />,
      children: [
        hasAny(["accounting.view", "finance.view"]) && { key: "/accounting/enquiry", label: "Enquiry", icon: <FileSearchOutlined /> },
        hasAny(["accounting.view", "finance.view"]) && { key: "/accounting", label: "Finance & Accounting", icon: <SafetyOutlined /> },
        hasAny(["commission.view"]) && { key: "/commission", label: "Commission", icon: <PercentageOutlined /> },
        hasAny(["ewaybill.view", "transport.read"]) && { key: "/ewaybill", label: "E-Way Bill", icon: <FileProtectOutlined /> },
      ].filter(Boolean) as MenuProps["items"],
    },

    // ── Inventory ─────────────────────────────────────────────────────────
    {
      key: "group-inventory",
      className: "nav-group-inventory",
      label: "Inventory",
      icon: <InboxOutlined />,
      children: [
        {
          key: "inv-transactions",
          label: "Transactions",
          icon: <AppstoreOutlined />,
          children: inventoryTransactions.filter(i => hasAny(i.permissionKeys) || i.disabled).map(leaf),
        },
        { key: "/inventory/enquiry", label: "Enquiry", icon: <FileSearchOutlined /> },
        { key: "__inv-reports", label: "Reports", icon: <BarChartOutlined />, disabled: true },
      ],
    },

    // ── Fleet Management ──────────────────────────────────────────────────
    {
      key: "group-fleet",
      className: "nav-group-fleet",
      label: "Fleet Management",
      icon: <CarOutlined />,
      children: [
        hasAny(["fleet.view"]) && { key: "/fleet", label: "Fleet Registry", icon: <CarOutlined /> },
        hasAny(["drivers.view"]) && { key: "/drivers", label: "Driver Registry", icon: <TeamOutlined /> },
        hasAny(["trips.view"]) && { key: "/trips", label: "Trip Planning", icon: <ClusterOutlined /> },
      ].filter(Boolean) as MenuProps["items"],
    },

    // ── Administration ────────────────────────────────────────────────────
    {
      key: "group-admin",
      className: "nav-group-admin",
      label: "Administration",
      icon: <SettingOutlined />,
      children: [
        hasAny(["parties.view"]) && { key: "/parties", label: "Party Registry", icon: <TeamOutlined /> },
        (isAdmin) && { key: "/users", label: "Users", icon: <UserOutlined /> },
        hasAny(["masterdata.view", "transport.read"]) && { key: "/master-data", label: "Master Data", icon: <DatabaseOutlined /> },
        (isAdmin) && { key: "/settings", label: "System Settings", icon: <SettingOutlined /> },
        (isAdmin) && { key: "/audit-logs", label: "Audit Log", icon: <SafetyOutlined /> },
      ].filter(Boolean) as MenuProps["items"],
    },
  ];
};

function resolveSelectedKey(pathname: string, availableKeys: string[]) {
  if (availableKeys.includes(pathname)) return pathname;
  const matched = availableKeys.find((key) => key !== "/" && pathname.startsWith(key));
  return matched ?? "/";
}

// Global cross-page navigation shortcuts (work from any page, any state).
// Convention: Ctrl+Shift+Letter  (won't clash with Alt+N / Ctrl+S page shortcuts)
export const NAV_SHORTCUTS: Array<{ combo: string; path: string; label: string }> = [
  { combo: "Ctrl+Shift+C", path: "/consignments",     label: "Consignment Booking" },
  { combo: "Ctrl+Shift+L", path: "/lorry-hire",       label: "Lorry Hire" },
  { combo: "Ctrl+Shift+M", path: "/manifests",        label: "Lorry Manifest" },
  { combo: "Ctrl+Shift+A", path: "/manifest-arrivals",label: "Manifest Arrival" },
  { combo: "Ctrl+Shift+G", path: "/gate-pass",        label: "Gate Pass" },
  { combo: "Ctrl+Shift+O", path: "/operations",       label: "Operations Utilities" },
  { combo: "Ctrl+Shift+B", path: "/reports",          label: "Reports" },
  { combo: "Ctrl+Shift+P", path: "/parties",          label: "Party Registry" },
  { combo: "Ctrl+Shift+S", path: "/settings",         label: "System Settings" },
  { combo: "Ctrl+Shift+D", path: "/bilty-stationery",label: "Document Stationery" },
];

export const ControlRoomLayout = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { mode, navMode, uiVariant, accent, siderCollapsed, setSiderCollapsed, toggleMode } = useThemeStore();
  const { user, branchId, branchName, activeRole, setActiveRole, clearSession } = useAuthStore();
  const { setActiveFy } = useFyStore();
  const [openKeys, setOpenKeys] = useState<string[]>(["group-transportation"]);
  const siderScrollRef = useRef<HTMLDivElement | null>(null);

  // ── Global cross-page keyboard navigation ─────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.shiftKey) return;
      const key = e.key.toUpperCase();
      const match = NAV_SHORTCUTS.find(s => s.combo.endsWith(`+${key}`));
      if (!match) return;
      // Only block if an overlay (modal/drawer) with data-overlay-keyboard-lock is open
      const overlayOpen = !!document.querySelector("[data-overlay-keyboard-lock]");
      if (overlayOpen) return;
      e.preventDefault();
      navigate(match.path);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  useEffect(() => {
    if (!activeRole && user?.roles?.length) {
      setActiveRole(user.roles[0]);
    }
  }, [activeRole, setActiveRole, user?.roles]);

  // Load active financial year on app startup
  useEffect(() => {
    const loadActiveFy = async () => {
      try {
        const activeFy = await transportApi.getActiveFinancialYear();
        if (activeFy?.id && activeFy?.code) {
          setActiveFy(activeFy.id, activeFy.code);
        }
      } catch {
        // Silently fail if no active FY (user will see warning on FY tab)
      }
    };
    void loadActiveFy();
  }, [setActiveFy]);

  const palette = getTransportPalette(accent, mode);

  const cssVars = useMemo<CSSProperties>(
    () =>
      ({
        "--app-bg": palette.appBackground,
        "--panel-bg": palette.panelBackground,
        "--elevated-bg": palette.elevatedBackground,
        "--header-bg": palette.headerBackground,
        "--sider-bg": palette.siderBackground,
        "--sider-text": palette.siderText,
        "--primary": palette.primary,
        "--info": palette.info,
        "--success": palette.success,
        "--warning": palette.warning,
        "--error": palette.error
      }) as CSSProperties,
    [palette]
  );

  const notificationItems: MenuProps["items"] = [
    { key: "n-empty", label: "No new notifications" }
  ];

  const normalizedPermissions = useMemo(
    () => (user?.permissions ?? []).map((permission) => permission.toLowerCase()),
    [user?.permissions]
  );
  const normalizedRoles = useMemo(
    () => (user?.roles ?? []).map((role) => role.toLowerCase()),
    [user?.roles]
  );
  const isAdminByRole = normalizedRoles.includes("administrator") || normalizedRoles.includes("superadmin") || normalizedRoles.includes("admin");
  const branchLabel = isAdminByRole
    ? "Welcome Admin"
    : `Logged in at ${branchName?.trim() || (branchId ? `Branch ${branchId}` : "Branch not assigned")}`;

  const builtMenuItems = useMemo<MenuProps["items"]>(
    () => menuItems(isAdminByRole || normalizedPermissions.length === 0, normalizedPermissions),
    [isAdminByRole, normalizedPermissions]
  );
  // Root-level menu: Operations rendered as a leaf (no inline expand) so clicking
  // it pushes into the drill-down panel instead of expanding inline.
  const activeMenuItems = useMemo<MenuProps["items"]>(() => {
    const removeInactive = (items: MenuProps["items"]): MenuProps["items"] => (items ?? []).flatMap((item: any) => {
      if (!item || item.disabled || String(item.key ?? "").startsWith("__")) return [];
      if (!item.children) return [item];
      const children = removeInactive(item.children);
      return children?.length ? [{ ...item, children }] : [];
    });
    return removeInactive(builtMenuItems);
  }, [builtMenuItems]);

  const searchItems = useMemo<SearchNavNode[]>(() => {
    // Keep the same hierarchy and ordering as the permission-filtered sidebar.
    const toSearchTree = (items: MenuProps["items"]): SearchNavNode[] => {
      if (!items) return [];
      return items.flatMap((item: any) => {
        if (!item || item.disabled || (item.key as string)?.startsWith("__placeholder__")) return [];
        if ((item.key as string)?.startsWith("__")) return [];
        const children = toSearchTree(item.children);
        if (item.children && !children.length) return [];
        return [{ key: item.key as string, label: item.label as string, icon: item.icon, ...(children.length ? { children } : {}) }];
      });
    };
    return toSearchTree(activeMenuItems);
  }, [activeMenuItems]);

  const handleMenuOpenChange = (keys: string[]) => {
    if (siderCollapsed) return;
    const openedKey = keys.find((key) => !openKeys.includes(key));
    setOpenKeys(keys);
    if (!openedKey) return;
    window.setTimeout(() => {
      const region = siderScrollRef.current;
      if (!region) return;
      const submenus = Array.from(region.querySelectorAll<HTMLElement>(".ant-menu-submenu"));
      const openSubmenus = submenus.filter((element) => element.classList.contains("ant-menu-submenu-open"));
      const opened = submenus.find((element) => element.getAttribute("data-menu-id")?.endsWith(openedKey))
        ?? openSubmenus[openSubmenus.length - 1];
      if (!opened) return;
      const regionRect = region.getBoundingClientRect();
      const title = opened.querySelector<HTMLElement>(":scope > .ant-menu-submenu-title");
      const titleRect = (title ?? opened).getBoundingClientRect();
      if (titleRect.bottom > regionRect.bottom - 8) {
        region.scrollTo({ top: region.scrollTop + titleRect.bottom - regionRect.bottom + 12, behavior: "smooth" });
      } else if (titleRect.top < regionRect.top + 8) {
        region.scrollTo({ top: region.scrollTop - (regionRect.top - titleRect.top) - 12, behavior: "smooth" });
      }
    }, 220);
  };

  const selectedKey = resolveSelectedKey(location.pathname, ALL_LEAF_PATHS);

  // Auto-drill into Operations when the active route lives there;
  // exit when navigating elsewhere; collapse resets to root.
  useEffect(() => {
    // Auto-open the parent submenu that contains the selected key
    const findParentKeys = (items: MenuProps["items"], target: string, parents: string[] = []): string[] => {
      if (!items) return [];
      for (const item of items as any[]) {
        if (!item) continue;
        if (item.key === target) return parents;
        if (item.children) {
          const found = findParentKeys(item.children, target, [...parents, item.key]);
          if (found.length || item.children.some((c: any) => c?.key === target)) return [...parents, item.key];
        }
      }
      return [];
    };
    const parentKeys = findParentKeys(activeMenuItems, selectedKey);
    if (parentKeys.length > 0) {
      setOpenKeys((prev) => {
        const merged = [...new Set([...prev, ...parentKeys])];
        return merged.length !== prev.length ? merged : prev;
      });
    }
  }, [activeMenuItems, selectedKey]);

  const profileName = useMemo(() => {
    const raw = (user?.displayName || user?.username || "User").trim();
    if (!raw.includes("@")) return raw;

    const local = raw.split("@")[0] ?? "User";
    const clean = local.replace(/[._-]+/g, " ").replace(/\d+/g, " ").replace(/\s+/g, " ").trim();
    if (!clean) return "User";

    return clean
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }, [user?.displayName, user?.username]);

  // ── User dropdown items (reactive to theme/navMode state) ───────────────────
  const userMenuItems = useMemo<MenuProps["items"]>(
    () => [
      { key: "profile", icon: <UserOutlined />, label: "My Profile" },
      { key: "settings", icon: <SettingOutlined />, label: "Settings" },
      { type: "divider" },
      { key: "logout", icon: <LogoutOutlined />, label: "Sign Out" }
    ],
    []
  );

  const isSidebar = navMode === "sidebar";

  return (
    <div
      className={`control-room ${mode === "dark" ? "mode-dark" : "mode-light"}${uiVariant === "highVisibility" ? " ui-high-visibility" : ""}`}
      style={cssVars}
    >
      <Layout className="control-layout">

        {/* ── Sidebar navigation ─────────────────────────────────────── */}
        {isSidebar && (
          <Sider
            collapsible
            collapsed={siderCollapsed}
            onCollapse={setSiderCollapsed}
            width={276}
            collapsedWidth={76}
            className="control-sider"
            trigger={null}
            style={siderCollapsed ? { overflow: "visible" } : undefined}
          >
            <div className="brand-row">
              <div className="brand-icon">
                <CarOutlined style={{ color: "var(--sider-text)" }} />
              </div>
              {!siderCollapsed && (
                <Typography.Title level={5} className="brand-title">
                  Transport ERP
                </Typography.Title>
              )}
            </div>

            <div ref={siderScrollRef} className="sider-scroll-region" style={siderCollapsed ? { overflow: "visible" } : undefined}>
              <Menu
                mode="inline"
                className="control-menu"
                inlineCollapsed={siderCollapsed}
                inlineIndent={18}
                items={activeMenuItems}
                selectedKeys={[selectedKey]}
                openKeys={siderCollapsed ? undefined : openKeys}
                onOpenChange={(keys) => handleMenuOpenChange(keys as string[])}
                onClick={({ key }) => navigate(String(key))}
              />
            </div>

            {!siderCollapsed ? <div className="side-footer">Transport ERP Suite v2.0</div> : null}
          </Sider>
        )}

        <Layout className="control-main-layout">
          <Header className={`control-header${isSidebar ? "" : " topbar-mode"}`}>
            <div className="header-left">
              {/* Collapse toggle — sidebar mode only */}
              {isSidebar && (
                <Button
                  type="text"
                  className="icon-button"
                  icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => setSiderCollapsed(!siderCollapsed)}
                />
              )}

              {/* Brand logo — topbar mode only */}
              {!isSidebar && (
                <div className="brand-row-inline">
                  <CarOutlined className="brand-icon-inline" />
                  <Typography.Title level={5} className="brand-title-inline">
                    Transport ERP
                  </Typography.Title>
                </div>
              )}
              <GlobalSearch searchItems={searchItems} isDark={mode === "dark"} accentColor={palette.primary} />
              <div className="header-branch-label" title={branchLabel}>
                {branchLabel}
              </div>
            </div>

            {/* Horizontal navigation menu — topbar mode only */}
            {!isSidebar && (
              <div className="topbar-nav-wrap">
                <Menu
                  mode="horizontal"
                  className="topbar-nav-menu"
                  items={builtMenuItems}
                  selectedKeys={[selectedKey]}
                  onClick={({ key }) => navigate(String(key))}
                  getPopupContainer={() => document.body}
                />
              </div>
            )}

            <div className="header-right">
              <HelpDrawer />

              <div className="header-theme-switch" aria-label="Light and dark theme">
                <BulbOutlined />
                <Switch
                  size="small"
                  checked={mode === "dark"}
                  checkedChildren="Dark"
                  unCheckedChildren="Light"
                  onChange={() => toggleMode()}
                />
              </div>

              <Dropdown menu={{ items: notificationItems }} placement="bottomRight" trigger={["click"]}>
                <Badge count={0} size="small">
                  <Button shape="circle" className="icon-button" icon={<BellOutlined />} />
                </Badge>
              </Dropdown>

              <Dropdown
                menu={{
                  items: userMenuItems,
                  onClick: ({ key }) => {
                    if (key === "logout") {
                      clearSession();
                      navigate("/login", { replace: true });
                    } else if (key === "settings") {
                      navigate("/settings");
                    }
                  }
                }}
                placement="bottomRight"
                trigger={["click"]}
              >
                <Button type="text" className="profile-trigger">
                  <Space className="profile-chip" size={10}>
                    <Avatar icon={<UserOutlined />} />
                    <div className="profile-meta">
                      <div className="profile-name">{profileName}</div>
                      <div className="profile-role">{activeRole ?? "Administrator"}</div>
                    </div>
                  </Space>
                </Button>
              </Dropdown>
            </div>
          </Header>

          <Content className="control-content">{children}</Content>
        </Layout>
      </Layout>
    </div>
  );
};
